import { ClientEmployeeApplicationFragment } from "./EmployeeApplicationsTable.g";
import React from "react";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";
import { Tag } from "@design-system/Tag/Tag";
import { Text } from "@design-system/Text/Text";

import { createTableConfiguration } from "@utils/table/TableConfigurationSystem";
import { EmployeeApplicationFilterFormValues } from "@components/Forms/EmployeeApplicationFilterForm/EmployeeApplicationFilterForm";

import {
  EMPLOYEE_APPLICATION_TABLE_COLUMN_LABELS,
  EmployeeApplicationTableColumns,
  DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_ORDER,
  DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_SETTINGS,
} from "./UI/EmployeeApplicationsTableColumns";

/**
 * Default filter values for Employee Applications
 */
export const DEFAULT_EMPLOYEE_APPLICATION_FILTER_VALUES: EmployeeApplicationFilterFormValues = {
  parentCategoryId: undefined,
  createdAt_Gte: undefined,
  createdAt_Lte: undefined,
};

/**
 * Comprehensive table configuration for Employee Applications
 */
export const EmployeeApplicationsTableConfig = createTableConfiguration<
  ClientEmployeeApplicationFragment,
  EmployeeApplicationTableColumns
>({
  tableId: "employee-applications",
  columns: {
    [EmployeeApplicationTableColumns.applicationName]: {
      id: EmployeeApplicationTableColumns.applicationName,
      label:
        EMPLOYEE_APPLICATION_TABLE_COLUMN_LABELS[
          EmployeeApplicationTableColumns.applicationName
        ],
      fieldPath: "resource.productName",
      sortField: "resource__product_name",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown, row: ClientEmployeeApplicationFragment) => {
        return renderApplicationName(value, row, () => {});
      },
    },
    [EmployeeApplicationTableColumns.description]: {
      id: EmployeeApplicationTableColumns.description,
      label:
        EMPLOYEE_APPLICATION_TABLE_COLUMN_LABELS[
          EmployeeApplicationTableColumns.description
        ],
      fieldPath: "resource.productDescription",
      sortField: "resource__product_description",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderDescription(value);
      },
    },
    [EmployeeApplicationTableColumns.category]: {
      id: EmployeeApplicationTableColumns.category,
      label:
        EMPLOYEE_APPLICATION_TABLE_COLUMN_LABELS[
          EmployeeApplicationTableColumns.category
        ],
      fieldPath: "resource.publicResource.category.parent.name",
      sortable: false,
      type: "custom",
      defaultVisible: true,
      render: (value: unknown, row: ClientEmployeeApplicationFragment) => {
        return renderCategory(value, row);
      },
    },
    [EmployeeApplicationTableColumns.lastUsed]: {
      id: EmployeeApplicationTableColumns.lastUsed,
      label:
        EMPLOYEE_APPLICATION_TABLE_COLUMN_LABELS[
          EmployeeApplicationTableColumns.lastUsed
        ],
      fieldPath: "resourceGroupLastSession",
      sortField: "tracking_sessions__max__updated_at",
      type: "date",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderLastUsed(value);
      },
    },
  },
  defaultColumnOrder: DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_ORDER,
  defaultColumnSettings: DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_SETTINGS,
});

/**
 * Helper function to get default filter values
 */
export const getDefaultFilterValues = (): EmployeeApplicationFilterFormValues => ({
  ...DEFAULT_EMPLOYEE_APPLICATION_FILTER_VALUES,
});

/**
 * Helper function to check if filters are active
 */
export const areFiltersActive = (filterValues: EmployeeApplicationFilterFormValues): boolean => {
  return Boolean(
    filterValues.parentCategoryId ||
      filterValues.createdAt_Gte ||
      filterValues.createdAt_Lte,
  );
};

/**
 * Custom render functions for specific columns
 */
export function renderApplicationName(
  _value: unknown,
  row: ClientEmployeeApplicationFragment,
  onApplicationDashboardClick: (applicationId: string) => void,
): React.ReactNode {
  return (
    <Clickable
      onClick={() => onApplicationDashboardClick(row.id)}
      outline="sm"
      stretch
    >
      <Table.CompanyCell
        name={row.resource?.productName ?? ""}
        subtext={row.resource?.vendorName || undefined}
        signedLogoUrl={
          row.resource?.publicResource?.signedLogoUrl || undefined
        }
        domain={row.resource?.productDomainName ?? ""}
      />
    </Clickable>
  );
}

export function renderDescription(value: unknown): React.ReactNode {
  return (
    <Text truncate maxWidth={200}>
      {String(value || "")}
    </Text>
  );
}

export function renderCategory(
  value: unknown,
  row: ClientEmployeeApplicationFragment,
): React.ReactNode {
  const parentName = value;
  const categoryName = row.resource?.publicResource?.category?.name;
  const displayName = parentName || categoryName;
  return (
    <Box>
      <Tag color="secondary" text={String(displayName)} />
    </Box>
  );
}

export function renderLastUsed(value: unknown): React.ReactNode {
  return (
    <Box>
      <Table.DateCell date={new Date(value as string)} dayOnly />
    </Box>
  );
}

export function renderActions(
  _value: unknown,
  row: ClientEmployeeApplicationFragment,
  onDashboardClick: (applicationId: string) => void,
  onRecordClick: (applicationId: string) => void,
  onUsersClick: (applicationId: string) => void,
): React.ReactNode {
  return (
    <Box gap={2}>
      <Dropdown
        trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
      >
        <Dropdown.Option
          label="Dashboard"
          icon="faGrip"
          onSelect={() => onDashboardClick(row.id)}
        />
        <Dropdown.Option
          label="Record"
          icon="faFileInvoice"
          onSelect={() => onRecordClick(row.id)}
        />
        <Dropdown.Option
          label="Users"
          icon="faUser"
          onSelect={() => onUsersClick(row.id)}
        />
      </Dropdown>
    </Box>
  );
}